<template>
  <!--
    Bộ lọc "Tên xuất VAT" cho modal kiểm tra/phát hành hóa đơn FAST.
    Nút nhãn + popover: 3 tab (Tất cả / Có IMEI / Không IMEI) · ô tìm KHÔNG dấu · "Chọn tất cả" + cột SL
    · danh sách tên VAT (checkbox + badge IMEI + số lượng ×N) · footer Xóa lọc / Hủy / OK.
    v-model = MẢNG tên VAT đang chọn (đã commit); rỗng = KHÔNG lọc. Cha tự lọc hóa đơn theo tên này.
    options = [{ name, isImei, qty }] (gộp từ toàn bộ dòng — cha suy ra).
  -->
  <b-dropdown
    ref="dd"
    variant="light"
    size="sm"
    no-caret
    lazy
    menu-class="vat-filter-menu"
    toggle-class="vat-filter-toggle"
    @show="onOpen"
  >
    <template #button-content>
      <span class="vat-filter-btn" :class="{ 'is-active': isActive }">
        <i class="fas fa-filter mr-2" aria-hidden="true"></i>
        <span class="vat-filter-btn-label">Tên xuất VAT</span>
        <span
          v-if="isActive"
          class="vat-filter-count"
          :title="`Đang lọc ${value.length} tên`"
          >{{ value.length }}</span
        >
        <i class="fas fa-chevron-down ml-2 vat-filter-caret" aria-hidden="true"></i>
      </span>
    </template>

    <b-dropdown-form class="vat-filter-form" @submit.prevent>
      <!-- Tabs IMEI / không IMEI -->
      <div class="vat-filter-tabs">
        <button
          v-for="t in tabs"
          :key="t.key"
          type="button"
          class="vat-filter-tab"
          :class="{ 'is-on': tab === t.key }"
          @click="selectTab(t.key)"
        >
          {{ t.label }} ({{ t.count }})
        </button>
      </div>

      <b-form-input
        v-model="search"
        size="sm"
        class="vat-filter-search"
        placeholder="Tìm tên xuất VAT, gõ không dấu được"
        autocomplete="off"
      />

      <!-- Chọn tất cả + nhãn cột SL -->
      <div class="vat-filter-head">
        <div
          class="vat-filter-item vat-filter-all font-weight-bold"
          @click="toggleSelectAllRow"
        >
          <i class="vat-filter-check" :class="selectAllIconClass"></i>
          <span class="ml-2 vat-filter-label"
            >Chọn tất cả ({{ visibleOptions.length }} tên)</span
          >
        </div>
        <span class="vat-filter-sl-head">SL</span>
      </div>

      <div class="vat-filter-list">
        <div
          v-for="opt in visibleOptions"
          :key="opt.key"
          class="vat-filter-item"
          :class="{ 'is-selected': isChecked(opt.key) }"
          :title="opt.name"
          @click="toggleItem(opt.key)"
        >
          <i
            class="vat-filter-check"
            :class="
              isChecked(opt.key)
                ? 'fas fa-check-square text-primary'
                : 'far fa-square text-muted'
            "
          ></i>
          <span class="ml-2 vat-filter-label">{{ opt.displayName }}</span>
          <span v-if="opt.isImei" class="vat-filter-imei">IMEI</span>
          <span class="vat-filter-qty">×{{ opt.qty }}</span>
        </div>
        <div
          v-if="!visibleOptions.length"
          class="text-muted small px-2 py-3 text-center"
        >
          Không có tên xuất VAT khớp
        </div>
      </div>

      <div class="vat-filter-actions">
        <button type="button" class="vat-filter-clear" @click="clearFilter">
          Xóa lọc
        </button>
        <div class="vat-filter-actions-right">
          <b-button
            size="sm"
            variant="secondary"
            class="font-weight-bolder"
            @click="close"
          >
            Hủy
          </b-button>
          <b-button
            size="sm"
            variant="primary"
            class="font-weight-bolder ml-2"
            @click="applyFilter"
          >
            OK
          </b-button>
        </div>
      </div>
    </b-dropdown-form>
  </b-dropdown>
</template>

<script>
import { xoa_dau } from '@/utils/common';

export default {
  name: 'VatNameFilter',
  props: {
    // [{ name, isImei, qty }] — gộp từ toàn bộ dòng (cha suy ra).
    options: {
      type: Array,
      default: () => [],
    },
    // v-model: mảng tên VAT đã chọn (đã commit). Rỗng = không lọc.
    value: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      search: '',
      // 'all' | 'imei' | 'nonimei'
      tab: 'all',
      // Bản nháp lựa chọn, chỉ commit khi bấm OK.
      draft: [],
    };
  },
  computed: {
    // Tên hiển thị gọn (tên rỗng → nhãn rõ ràng). Giữ `name` gốc làm khóa lọc.
    normalizedOptions() {
      return (this.options || []).map((opt) => ({
        ...opt,
        displayName: opt.name ? String(opt.name) : '(Thiếu tên VAT)',
      }));
    },
    tabs() {
      const imei = this.normalizedOptions.filter((o) => o.isImei).length;
      return [
        { key: 'all', label: 'Tất cả', count: this.normalizedOptions.length },
        { key: 'imei', label: 'Có IMEI', count: imei },
        {
          key: 'nonimei',
          label: 'Không IMEI',
          count: this.normalizedOptions.length - imei,
        },
      ];
    },
    tabOptions() {
      if (this.tab === 'imei') {
        return this.normalizedOptions.filter((o) => o.isImei);
      }
      if (this.tab === 'nonimei') {
        return this.normalizedOptions.filter((o) => !o.isImei);
      }
      return this.normalizedOptions;
    },
    visibleOptions() {
      const keyword = xoa_dau((this.search || '').trim().toLowerCase());
      if (!keyword) return this.tabOptions;
      return this.tabOptions.filter((opt) =>
        xoa_dau(String(opt.displayName).toLowerCase()).includes(keyword),
      );
    },
    allVisibleSelected() {
      return (
        this.visibleOptions.length > 0 &&
        this.visibleOptions.every((opt) => this.draft.includes(opt.key))
      );
    },
    someVisibleSelected() {
      return (
        !this.allVisibleSelected &&
        this.visibleOptions.some((opt) => this.draft.includes(opt.key))
      );
    },
    selectAllIconClass() {
      if (this.allVisibleSelected) return 'fas fa-check-square text-primary';
      if (this.someVisibleSelected) return 'fas fa-minus-square text-primary';
      return 'far fa-square text-muted';
    },
    isActive() {
      return Array.isArray(this.value) && this.value.length > 0;
    },
  },
  methods: {
    // Đổi tab = CHỈ đổi phạm vi HIỂN THỊ (R2-2), KHÔNG reset `draft` để không mất lựa chọn đã khôi phục.
    // Chống footgun F3 nằm ở `applyFilter` (chỉ coi "không lọc" khi tích ĐÚNG TOÀN BỘ key) + "Chọn tất cả"
    // thao tác theo tập visible của tab (muốn lọc riêng 1 tab → dùng "Chọn tất cả" trên tab bù để bỏ chọn).
    selectTab(key) {
      this.tab = key;
    },
    onOpen() {
      this.search = '';
      this.tab = 'all';
      const allKeys = this.normalizedOptions.map((opt) => opt.key);
      // Đang lọc → khôi phục tập đã chọn NHƯNG chỉ giữ key còn tồn tại (F9: options đổi giữa các lần
      // validate). Chưa lọc → tích hết (giống Excel = không lọc).
      if (Array.isArray(this.value) && this.value.length) {
        const keySet = new Set(allKeys);
        this.draft = this.value.filter((k) => keySet.has(k));
      } else {
        this.draft = allKeys;
      }
    },
    isChecked(key) {
      return this.draft.includes(key);
    },
    toggleItem(key) {
      if (this.draft.includes(key)) {
        this.draft = this.draft.filter((item) => item !== key);
      } else {
        this.draft = [...this.draft, key];
      }
    },
    toggleSelectAllRow() {
      const visibleKeys = this.visibleOptions.map((opt) => opt.key);
      if (this.allVisibleSelected) {
        const visibleSet = new Set(visibleKeys);
        this.draft = this.draft.filter((key) => !visibleSet.has(key));
      } else {
        this.draft = Array.from(new Set([...this.draft, ...visibleKeys]));
      }
    },
    applyFilter() {
      let selected = [...this.draft];
      // "Không lọc" CHỈ khi đã tích ĐÚNG TOÀN BỘ key (so cùng phạm vi toàn cục với select-all) → rỗng.
      const allKeys = new Set(this.normalizedOptions.map((opt) => opt.key));
      if (selected.length === allKeys.size && selected.every((k) => allKeys.has(k))) {
        selected = [];
      }
      this.$emit('input', selected);
      this.close();
    },
    clearFilter() {
      this.$emit('input', []);
      this.draft = this.normalizedOptions.map((opt) => opt.key);
      this.search = '';
      this.tab = 'all';
      this.close();
    },
    close() {
      if (this.$refs.dd) this.$refs.dd.hide(true);
    },
  },
};
</script>

<style scoped>
::v-deep .vat-filter-toggle.btn {
  display: inline-flex;
  align-items: center;
  height: 34px;
  padding: 0 12px !important;
  border: 1px solid #d0d5dd !important;
  border-radius: 8px !important;
  background: #ffffff !important;
  color: #344054 !important;
  font-weight: 600;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05) !important;
}
::v-deep .vat-filter-toggle.btn:hover,
::v-deep .vat-filter-toggle.btn:focus {
  border-color: #3699ff !important;
  color: #3699ff !important;
  background: #f4f9ff !important;
}
.vat-filter-btn {
  display: inline-flex;
  align-items: center;
  font-size: 13px;
  line-height: 1;
}
.vat-filter-btn.is-active {
  color: #3699ff;
}
.vat-filter-caret {
  font-size: 10px;
  opacity: 0.7;
}
.vat-filter-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  margin-left: 6px;
  padding: 0 5px;
  border-radius: 9px;
  background: #3699ff;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
}
::v-deep .vat-filter-menu {
  width: 680px !important;
  min-width: 680px !important;
  max-width: 92vw !important;
  padding: 0;
  border: 1px solid #e4e6ef;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(16, 24, 40, 0.14);
}
::v-deep .vat-filter-menu .b-dropdown-form {
  display: block;
  width: 100%;
  margin: 0;
  padding: 12px !important;
}
.vat-filter-tabs {
  display: flex;
  gap: 6px;
  padding: 4px;
  margin-bottom: 10px;
  background: #f1f3f9;
  border-radius: 8px;
}
.vat-filter-tab {
  flex: 1 1 0;
  padding: 7px 8px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #4b5563;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.vat-filter-tab.is-on {
  background: #ffffff;
  color: #1f2a44;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.1);
}
.vat-filter-search {
  margin-bottom: 8px;
}
.vat-filter-head {
  display: flex;
  align-items: center;
  padding-bottom: 6px;
  margin-bottom: 4px;
  border-bottom: 1px solid #ebedf3;
}
.vat-filter-sl-head {
  margin-left: auto;
  padding-right: 4px;
  font-size: 11px;
  font-weight: 700;
  color: #98a2b3;
  font-variant-numeric: tabular-nums;
}
.vat-filter-list {
  max-height: 320px;
  overflow-y: auto;
}
.vat-filter-item {
  display: flex;
  align-items: center;
  padding: 7px 6px;
  cursor: pointer;
  border-radius: 6px;
}
.vat-filter-item:hover {
  background-color: #f4f6fb;
}
.vat-filter-item.is-selected {
  background-color: #eaf3ff;
}
.vat-filter-check {
  width: 16px;
  text-align: center;
  flex: 0 0 auto;
}
.vat-filter-label {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  color: #1f2a44;
}
.vat-filter-all .vat-filter-label {
  color: #111827;
}
.vat-filter-imei {
  flex: 0 0 auto;
  margin-left: 8px;
  padding: 1px 7px;
  border-radius: 6px;
  background: #e6f0ff;
  color: #2f6fd6;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.3px;
}
.vat-filter-qty {
  flex: 0 0 auto;
  margin-left: 8px;
  min-width: 32px;
  text-align: right;
  color: #667085;
  font-size: 12.5px;
  font-variant-numeric: tabular-nums;
}
.vat-filter-actions {
  display: flex;
  align-items: center;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #ebedf3;
}
.vat-filter-actions-right {
  margin-left: auto;
  display: flex;
  align-items: center;
}
.vat-filter-clear {
  border: 0;
  background: transparent;
  color: #e4572e;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  padding: 4px 2px;
}
.vat-filter-clear:hover {
  text-decoration: underline;
}
</style>
